import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { OrderDetailSheet } from '@/components/orders/OrderDetailSheet';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useSimulation } from '@/hooks/useSimulation';
import { Search, ArrowUpDown, FileText, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t, getLocale } from '@/i18n';

const statusColors = {
  'pending': 'bg-gray-100 text-gray-700',
  'confirmed': 'bg-blue-100 text-blue-700',
  'in-production': 'bg-yellow-100 text-yellow-700',
  'produced': 'bg-purple-100 text-purple-700',
  'dispatched': 'bg-green-100 text-green-700',
  'delivered': 'bg-emerald-100 text-emerald-700',
};

const statusLabels = {
  'pending': t('orders.pending'), 'confirmed': t('orders.confirmed'), 'in-production': t('orders.inProduction'),
  'produced': t('orders.produced'), 'dispatched': t('orders.dispatched'), 'delivered': t('orders.delivered'),
};

const allStatuses = ['pending', 'confirmed', 'in-production', 'produced', 'dispatched', 'delivered'];

function getOrderProgress(order) {
  if (!order.items || order.items.length === 0) return 0;
  const totalQty = order.items.reduce((s, i) => s + (i.quantity || 0), 0);
  const totalProduced = order.items.reduce((s, i) => s + (i.produced || 0), 0);
  return totalQty > 0 ? Math.round((totalProduced / totalQty) * 100) : 0;
}

export default function Objednavky() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orders, getTruckById } = useSimulation();

  const highlightId = searchParams.get('highlight');

  const [selectedOrder, setSelectedOrder] = useState(() => {
    if (highlightId) return orders.find(o => o.id === highlightId) || null;
    return null;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState('desc');

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = {};
    allStatuses.forEach(s => { counts[s] = 0; });
    orders.forEach(o => { counts[o.status] = (counts[o.status] || 0) + 1; });
    return counts;
  }, [orders]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const totalValue = orders.reduce((s, o) => s + (o.totalValue || 0), 0);
    const avgValue = orders.length > 0 ? Math.round(totalValue / orders.length) : 0;
    const delivered = orders.filter(o => o.status === 'delivered' || o.status === 'dispatched').length;
    const onTimeRate = orders.length > 0 ? Math.round((delivered / orders.length) * 100) : 0;
    return { total: orders.length, totalValue, avgValue, onTimeRate };
  }, [orders]);

  // Filter & sort
  const filteredOrders = useMemo(() => {
    let result = orders.filter(order => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q ||
        order.id.toLowerCase().includes(q) ||
        order.customer?.name?.toLowerCase().includes(q) ||
        order.items?.some(i => i.material.toLowerCase().includes(q));
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'id') cmp = a.id.localeCompare(b.id);
      else if (sortField === 'customer') cmp = (a.customer?.name || '').localeCompare(b.customer?.name || '');
      else if (sortField === 'value') cmp = (a.totalValue || 0) - (b.totalValue || 0);
      else if (sortField === 'status') cmp = allStatuses.indexOf(a.status) - allStatuses.indexOf(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [orders, searchQuery, statusFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Get linked trucks for selected order
  const linkedTrucks = useMemo(() => {
    if (!selectedOrder?.truckIds) return [];
    return selectedOrder.truckIds.map(getTruckById).filter(Boolean);
  }, [selectedOrder, getTruckById]);

  const SortHeader = ({ field, children }) => (
    <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort(field)}>
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={cn('h-3 w-3', sortField === field ? 'text-primary' : 'text-muted-foreground/40')} />
      </div>
    </TableHead>
  );

  return (
    <PageLayout title={t('orders.title')} concept>
      <div className="space-y-6">
        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: FileText, label: t('orders.totalOrders'), value: summaryStats.total, color: 'text-blue-600 bg-blue-50' },
            { icon: DollarSign, label: t('orders.totalValue'), value: `${(summaryStats.totalValue / 1000).toFixed(0)}k EUR`, color: 'text-green-600 bg-green-50' },
            { icon: TrendingUp, label: t('orders.avgOrder'), value: `${summaryStats.avgValue.toLocaleString()} EUR`, color: 'text-purple-600 bg-purple-50' },
            { icon: Clock, label: t('orders.fulfillmentRate'), value: `${summaryStats.onTimeRate}%`, color: 'text-amber-600 bg-amber-50' },
          ].map(stat => (
            <Card key={stat.label} className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center', stat.color)}>
                  <stat.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-lg font-bold">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Status summary */}
        <div className="flex flex-wrap gap-2">
          {allStatuses.map(s => (
            <Badge
              key={s}
              variant="outline"
              className={cn(
                'gap-1.5 text-xs cursor-pointer transition-colors',
                statusFilter === s ? statusColors[s] : 'hover:bg-muted',
              )}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            >
              {statusLabels[s]}: <span className="font-bold">{statusCounts[s]}</span>
            </Badge>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-4 bg-card rounded-xl border border-border p-4 shadow-sm">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('orders.searchPlaceholder')}
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t('orders.statusFilter')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('orders.allStatuses')}</SelectItem>
              {allStatuses.map(s => (
                <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="overflow-x-auto">
          <Table className="min-w-[700px]">
            <TableHeader>
              <TableRow>
                <SortHeader field="id">{t('orders.number')}</SortHeader>
                <SortHeader field="customer">{t('orders.customer')}</SortHeader>
                <TableHead>{t('orders.items')}</TableHead>
                <TableHead>{t('orders.progress')}</TableHead>
                <SortHeader field="value">{t('orders.value')}</SortHeader>
                <TableHead>{t('orders.delivery')}</TableHead>
                <SortHeader field="status">{t('orders.status')}</SortHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => {
                const progress = getOrderProgress(order);
                const deliveryDate = order.requestedDelivery ? new Date(order.requestedDelivery) : null;
                const isUrgent = deliveryDate && (deliveryDate - new Date()) < 3 * 86400000 && !['delivered', 'dispatched'].includes(order.status);

                return (
                  <TableRow
                    key={order.id}
                    className={cn(
                      'cursor-pointer hover:bg-primary/5 transition-colors duration-150 even:bg-muted/20',
                      highlightId === order.id && 'bg-primary/10 ring-1 ring-primary/30',
                    )}
                    onClick={() => setSelectedOrder(order)}
                  >
                    <TableCell className="font-mono text-sm font-medium">{order.id}</TableCell>
                    <TableCell>
                      <div className="font-medium">{order.customer?.name}</div>
                      {order.customer?.contact && (
                        <div className="text-xs text-muted-foreground">{order.customer.contact}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {order.items?.map(i => `${i.quantity}${i.unit} ${i.material}`).join(', ')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-20">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={progress}
                            className={cn('h-1.5 flex-1', progress > 80 ? '[&>div]:bg-green-500' : progress > 40 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-gray-400')}
                          />
                          <span className="text-xs text-muted-foreground w-8">{progress}%</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{order.totalValue?.toLocaleString()} EUR</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        {isUrgent && <Clock className="h-3 w-3 text-red-500" />}
                        <span className={isUrgent ? 'text-red-600 font-medium' : ''}>
                          {deliveryDate ? deliveryDate.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' }) : '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <div className="text-xs text-muted-foreground text-center">
          {t('orders.showing')} {filteredOrders.length} {t('orders.of')} {orders.length} {t('orders.ordersCount')}
        </div>
      </div>

      {/* Order Detail Sheet */}
      <OrderDetailSheet
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onNavigateToTruck={(truckId) => {
          setSelectedOrder(null);
          navigate(`/kamiony?highlight=${truckId}`);
        }}
        linkedTrucks={linkedTrucks}
      />
    </PageLayout>
  );
}
